import React from 'react';
import { Animated, ScrollView } from 'react-native';

import ColorWrapper, { WithColor } from '@styles/colors';
import { scale } from '@components/frame';
import Svg from '@styles/icons/svg';

import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { LOGO_HEIGHT, LOGO_WIDTH } from '@components/tenantStatus/common/constants';

import { useManageOnboardingWizard } from './hooks/useManageOnboardingWizard';
import { useManageOnboardingWizardModal } from './hooks/useManageOnboardingWizardModal';
import { STEP } from './constants';
import WizardCard from './components/WizardCard';
import WizardProgress from './components/WizardProgress';
import WizardFooter from './components/WizardFooter';
import WizardStepTransition from './components/WizardStepTransition';
import WizardStepRenderer from './components/WizardStepRenderer';

const OnboardingWizard = ({ C }: WithColor) => {
  const {
    currentStep,
    form,
    submitting,
    onBack,
    onNext,
    onGetStarted,
    updateMachines,
    updateErp,
    updateGoals,
    updateTeam,
  } = useManageOnboardingWizard();

  const {
    renderedStepKey,
    stepTransitionKey,
    isRenderedCentered,
    contentOpacity,
    logoTranslateY,
    contentTranslateY,
    chromeOpacity,
  } = useManageOnboardingWizardModal({ currentStep });

  const isLastStep = currentStep === STEP.TEAM;
  const ids = TEST_IDS.tenantStatus.onboardingWizard;

  return (
    <WizardCard C={C} testID={ids.container} cardTestID={ids.card}>
      <Animated.View
        style={{
          alignItems: 'center',
          marginBottom: scale(20),
          transform: [{ translateY: logoTranslateY }],
        }}
      >
        <Svg
          icon="smartFactory"
          width={scale(LOGO_WIDTH)}
          height={scale(LOGO_HEIGHT)}
          color="Other/Light/Logo/Factory"
        />
      </Animated.View>

      <Animated.View
        pointerEvents={isRenderedCentered ? 'none' : 'auto'}
        style={{ marginBottom: scale(16), opacity: chromeOpacity }}
      >
        <WizardProgress C={C} currentStep={currentStep} testID={ids.progress} />
      </Animated.View>

      <Animated.View
        style={{
          flex: 1,
          opacity: contentOpacity,
          transform: [{ translateY: contentTranslateY }],
        }}
      >
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
          {/* Remount across the centered-logo boundary (welcome ↔ form, form ↔ submitted)
              so the new step mounts as the initial displayed layer — otherwise
              WizardStepTransition's internal crossfade lets the outgoing layer bleed
              through during the outer contentOpacity fade. */}
          <WizardStepTransition key={stepTransitionKey} stepKey={renderedStepKey}>
            <WizardStepRenderer
              C={C}
              step={renderedStepKey}
              form={form}
              onGetStarted={onGetStarted}
              onUpdateMachines={updateMachines}
              onUpdateErp={updateErp}
              onUpdateGoals={updateGoals}
              onUpdateTeam={updateTeam}
            />
          </WizardStepTransition>
        </ScrollView>
      </Animated.View>

      <Animated.View
        pointerEvents={isRenderedCentered ? 'none' : 'auto'}
        style={{ opacity: chromeOpacity }}
      >
        <WizardFooter
          C={C}
          isLastStep={isLastStep}
          onBack={onBack}
          onNext={onNext}
          submitting={submitting}
          testIDs={{ back: ids.backButton, next: ids.nextButton, submit: ids.submitButton }}
        />
      </Animated.View>
    </WizardCard>
  );
};

export default ColorWrapper(OnboardingWizard);
